import { cn } from "@/lib/utils";
import { Heart } from "lucide-react";
import { InputHTMLAttributes, useEffect, useRef, useState } from "react";

interface LoveReactionButtonProps extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "size"
> {
    /** 현재 사용자가 이미 좋아요를 눌렀는지 여부 */
    checked: boolean;
    /** 총 좋아요 개수 */
    count?: number;
    /** 크기 variant */
    size?: "sm" | "md";
    /** 비활성화 여부 (로그인 필요 등) */
    disabled?: boolean;
    className?: string;
}

/**
 * 댓글 좋아요(하트) 반응 토글 버튼 컴포넌트
 * BookmarkIconButton 패턴을 따라 checkbox input으로 구현
 * 즉각적인 시각적 피드백 제공
 *
 * @example
 * ```tsx
 * <LoveReactionButton
 *   checked={hasReacted}
 *   count={reactionCount}
 *   onChange={(e) => handleToggle(e.target.checked)}
 *   disabled={!isLoggedIn}
 * />
 * ```
 */
export const LoveReactionButton: React.FC<LoveReactionButtonProps> = ({
    checked = false,
    count = 0,
    size = "sm",
    disabled = false,
    className,
    onChange,
    ...props
}) => {
    const [displayCount, setDisplayCount] = useState(count);
    const [isAnimating, setIsAnimating] = useState(false);
    const [direction, setDirection] = useState<"up" | "down">("up");
    const prevCountRef = useRef(count);

    useEffect(() => {
        if (count !== prevCountRef.current) {
            setDirection(count > prevCountRef.current ? "up" : "down");
            setIsAnimating(true);

            // 짧은 딜레이 후 숫자 업데이트
            const timer = setTimeout(() => {
                setDisplayCount(count);
                setIsAnimating(false);
            }, 150);

            prevCountRef.current = count;
            return () => clearTimeout(timer);
        }
    }, [count]);

    const sizeClasses = {
        sm: "h-7",
        md: "h-8"
    };

    const iconSize = size === "sm" ? "size-3.5" : "size-4";
    const textSize = size === "sm" ? "text-xs" : "text-sm";

    return (
        <label
            className={cn(
                `group inline-flex cursor-pointer items-center gap-1.5
                rounded-md px-2.5 py-1 font-medium transition-all`,
                sizeClasses[size],
                // 체크되었을 때 스타일
                checked
                    ? `bg-red-50 text-red-600 hover:bg-red-100
                        dark:bg-red-900/20 dark:text-red-400
                        dark:hover:bg-red-900/30`
                    : `bg-transparent text-gray-600 hover:bg-gray-100
                        dark:text-gray-400 dark:hover:bg-gray-800`,
                // 비활성화 상태
                disabled && "cursor-not-allowed opacity-50",
                !disabled && "active:scale-95",
                className
            )}
            onClick={(e) => e.stopPropagation()}
        >
            <input
                className="hidden"
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                {...props}
            />

            {/* 하트 아이콘 - 체크되면 채워짐, 클릭 시 펄스 애니메이션 */}
            <Heart
                className={cn(
                    iconSize,
                    "transition-all duration-200",
                    checked && "scale-110 fill-current",
                    isAnimating && direction === "up" && "animate-pulse"
                )}
            />

            {/* 카운트 표시 - 슬라이드 애니메이션 */}
            {(count > 0 || displayCount > 0) && (
                <span
                    className={cn(
                        textSize,
                        "min-w-[1ch] text-center transition-all duration-150",
                        isAnimating &&
                            direction === "up" &&
                            "translate-y-1 opacity-0",
                        isAnimating &&
                            direction === "down" &&
                            "-translate-y-1 opacity-0"
                    )}
                >
                    {displayCount}
                </span>
            )}
        </label>
    );
};
